import { useState, useEffect } from 'react';
import { collection, query, where, getDoc, getDocs, doc } from 'firebase/firestore';
import { db } from '../firebase.js';
import * as utils from '../common/Utils.js';

export function computeDraftPicksFromDraft(draft, teams, draftTimes, players = []) {
  if (!draft || !draft.undraftedPlayers || !draft.draftOrder) return [];

  const undraftedPlayerCount = draft.undraftedPlayers.length;
  const draftOrder = draft.draftOrder.length > 0 ? draft.draftOrder : teams.map(t => t.id);
  const teamsInSeason = draftOrder.length; // use draftOrder as the authoritative team count
  const picks = [];

  let pickIndex = 0;
  let dir = 'asc';

  for (let i = 0; i < undraftedPlayerCount; i++) {
    picks.push({
      teamID: draftOrder[pickIndex],
      team: utils.getTeamForID(teams, draftOrder[pickIndex]),
      number: i + 1,
      playerID: '',
      player: null,
      onClock: false,
      currentPick: false,
      time: draftTimes[i] || null,
    });

    if (pickIndex === 0 && dir === 'desc') {
      dir = 'asc';
    } else if (pickIndex === teamsInSeason - 1 && dir === 'asc') {
      dir = 'desc';
    } else if (dir === 'asc') {
      pickIndex++;
    } else {
      pickIndex--;
    }
  }

  const draftedPlayers = draft.draftedPlayers || [];
  const isDraftComplete =
    draftedPlayers.length === undraftedPlayerCount &&
    draftedPlayers.every(id => id && id !== '');

  for (let i = 0; i < Math.min(draftedPlayers.length, picks.length); i++) {
    const playerID = draftedPlayers[i];
    picks[i].playerID = playerID || '';
    if (playerID && playerID !== '') {
      picks[i].player = utils.getPlayerByID(players, playerID) || null;
    }
  }

  if (!isDraftComplete) {
    const firstEmptyIdx = picks.findIndex(p => !p.playerID || p.playerID === '');
    if (firstEmptyIdx !== -1) {
      picks[firstEmptyIdx].currentPick = true;
      picks[firstEmptyIdx].onClock = true;
    }

    // Only mark later picks as on-the-clock due to missed windows once
    // the draft has actually started (at least one pick made).
    const draftStarted = picks.some(p => p.playerID && p.playerID !== '');
    if (draftStarted && firstEmptyIdx !== -1) {
      const now = new Date().toISOString();
      for (let i = firstEmptyIdx + 1; i < picks.length; i++) {
        const pick = picks[i];
        if ((!pick.playerID || pick.playerID === '') && pick.time && pick.time < now) {
          pick.onClock = true;
        }
      }
    }
  }

  return picks;
}

export function useDraftData(SEASON) {
  const [draft, setDraft] = useState({
    corePlayers: [],
    draftOrder: [],
    draftedPlayers: [],
    undraftedPlayers: [],
  });
  const [players, setPlayers] = useState([]);
  const [games, setGames] = useState([]);
  const [allGameStats, setAllGameStats] = useState([]);
  const [teams, setTeams] = useState([]);
  const [seasonTeams, setSeasonTeams] = useState([]);
  const [signups, setSignups] = useState([]);
  const [draftTimes, setDraftTimes] = useState([]);
  const [computedDraftPicks, setComputedDraftPicks] = useState([]);
  const [dataReady, setDataReady] = useState(false);
  const [draftExists, setDraftExists] = useState(false);

  useEffect(() => {
    loadData(); // eslint-disable-line react-hooks/exhaustive-deps
  }, [SEASON]); // re-fetch whenever the viewed season changes

  async function loadData() {
    setDataReady(false);

    // Fire all reads in parallel rather than awaiting each one in sequence.
    const [
      playerSnapshot,
      gameStatsSnapshot,
      gamesSnapshot,
      teamsSnapshot,
      seasonTeamsSnapshot,
      draftSnapshot,
      signupsSnapshot,
    ] = await Promise.all([
      getDocs(collection(db, 'players')),
      getDocs(collection(db, 'gameStats')),
      getDocs(collection(db, 'games')),
      getDocs(collection(db, 'teams')),
      getDocs(collection(db, 'seasonTeams')),
      getDoc(doc(db, 'draft', SEASON)),
      getDocs(query(collection(db, 'signups'), where('season', '==', SEASON))),
    ]);

    const players = playerSnapshot.docs.map(d => d.data());
    setPlayers(players);

    const allGameStats = [];
    gameStatsSnapshot.forEach(d => {
      const gameID = d.id;
      const stats = (d.data()['stats'] || []).map(s => ({ ...s, gameID }));
      allGameStats.push(...stats);
    });
    setAllGameStats(allGameStats);

    const games = gamesSnapshot.docs
      .map(d => ({ id: d.id, ...d.data(), date: d.data().date.toDate() }))
      .sort((a, b) => a.date - b.date);
    setGames(games);

    const teamsData = teamsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    const teams = seasonTeamsSnapshot.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(st => st.season == SEASON)
      .map(st => ({ ...st, ...teamsData.find(t => t.id === st.teamID) }));

    setTeams(teams);
    setSeasonTeams(teams);

    let draft = { corePlayers: [], draftOrder: [], draftedPlayers: [], undraftedPlayers: [] };
    let exists = false;
    let draftTimes = [];

    if (draftSnapshot.exists()) {
      exists = true;
      draft = draftSnapshot.data();
      if (draft.draftSchedule) {
        draftTimes = utils.generateTimeSlotsFromSchedule(draft.draftSchedule);
      }
    }

    setDraft(draft);
    setDraftExists(exists);
    setDraftTimes(draftTimes);
    setComputedDraftPicks(computeDraftPicksFromDraft(draft, teams, draftTimes, players));

    const signups = signupsSnapshot.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => {
        const da = new Date(a.created_at.seconds * 1000 + a.created_at.nanoseconds / 1000000);
        const db_ = new Date(b.created_at.seconds * 1000 + b.created_at.nanoseconds / 1000000);
        return db_ - da;
      });
    setSignups(signups);

    setDataReady(true);
  }

  return {
    draft,
    players,
    games,
    allGameStats,
    teams,
    seasonTeams,
    signups,
    draftTimes,
    computedDraftPicks,
    dataReady,
    draftExists,
    refresh: loadData,
  };
}
